import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';
import { AddCircle, CheckCircle, Edit, Delete, Cancel } from '@material-ui/icons';
import { DateViewModel } from 'common/genericTypes';
import { Button, Section, SmSpace } from 'm2m.components';
import moment, { Moment } from 'moment';
import React, { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { labelsEventosFinanceiros as labels } from 'common/registro/labels';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useValidarCampos, EtapaCadastro } from '../useValidarCampos';
import { localizedTextsMap } from 'utils/dataGridUtils';
import {
  DataGridPro,
  GridToolbarContainer,
  GridRowModesModel,
  GridRowsProp,
  GridRowModes,
  GridRowParams,
  MuiEvent,
  GridEventListener,
  GridRowModel,
  GridRowId,
  GridActionsCellItem,
  GridColumns,
  GridValueFormatterParams,
  GridSelectionModel,
  GridCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dataGridDiv: {
      height: 400,
      width: 550,
      '& .jurosError': {
        backgroundColor: theme.palette.error.main,
      },
    },
  })
);


interface PagamentoJurosSectionProps {
  onUpdateDates: (dateViewModel: DateViewModel[]) => void;
  dates: Moment[];
  minDate?: Moment;
  maxDate?: Moment;
}

export const PagamentoJurosSection: React.FC<PagamentoJurosSectionProps> = (props) => {
  const { onUpdateDates, dates, minDate, maxDate } = props;
  const classes = useStyles();

  const dispatch = useDispatch();
  const getValidarDiaUtil = useSelector((state: RootState) => state.feriadosNacionais.getValidarDiaUtil);

  const highlightErrors = useSelector((state: RootState) => state.debentureCadastro.highlightErrors);
  const camposInvalidos = useValidarCampos(EtapaCadastro.EventosFinanceiros);

  const [pageSize, setPageSize] = React.useState(20);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const [selectionModel, setSelectionModel] = React.useState<GridSelectionModel>([]);
  const [rows, setRows] = React.useState<GridRowsProp>([]);
  const [invalidDates, setInvalidDates] = React.useState<Record<string, boolean>>({});
  const [forceUpdate, setForceUpdate] = React.useState<number>(0);

  const updateForceUpdate = () => {
    setTimeout(() => {
      setForceUpdate(prev => prev + 1);
    }, 0);
  }

  const handleDeleteClick = (id: GridRowId) => () => {
    const updatedRows = rows.filter((row) => row.id !== id);
    setRows(updatedRows);
    handleRowsChange(updatedRows);
    updateForceUpdate();
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      const updatedRows = rows.filter((row) => row.id !== id);
      setRows(updatedRows);
      handleRowsChange(updatedRows);
    }
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  interface EditToolbarProps {
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
    setRowModesModel: (newModel: (oldModel: GridRowModesModel) => GridRowModesModel) => void;
  }

  function EditToolbar(props: EditToolbarProps) {
    const handleClick = () => {
      const id = uuidv4();
      setRows([...rows, { id, percentual: null, dataPagamentoJuros: null, isNew: true }]);
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: 'dataPagamentoJuros' } });
      updateForceUpdate();
    };

    const handleDeleteClick = () => {
      const updatedRows = rows.filter((row) => !selectionModel.some((sm) => sm === row.id));
      setRows(updatedRows);
      handleRowsChange(updatedRows);
      updateForceUpdate();
    };

    return (
      <GridToolbarContainer>
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={handleClick}
          startIcon={<AddCircle fontSize="small" />}
        >
          {'Novo pagamento de juros'}
        </Button>
        <Button
          color="error"
          size="small"
          onClick={handleDeleteClick}
          disabled={!selectionModel.length}
          startIcon={<Delete fontSize="small" />}
        >
          {'Excluir pagamentos de juros'}
        </Button>
      </GridToolbarContainer>
    );
  }

  const handleRowEditStart = (params: GridRowParams, event: MuiEvent<React.SyntheticEvent>) => {
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    const updatedRows = rows.map((row) => (row.id === newRow.id ? updatedRow : row));
    setRows(updatedRows);
    handleRowsChange(updatedRows);

    if (newRow.dataPagamentoJuros) {
      setTimeout(() => {
        validateSingleDate(newRow.id as string, newRow.dataPagamentoJuros as Date);
      }, 0);
    }

    updateForceUpdate();

    return updatedRow;
  };

  const handleRowsChange = (newRows: GridValidRowModel[]) => {
    const pagamentoJurosNew = newRows
      .filter((r) => r.dataPagamentoJuros)
      .map((x) => {
        const pagamentoJuros = {
          id: x.id,
          date: x.dataPagamentoJuros,
        } as DateViewModel;
        return pagamentoJuros;
      });

    onUpdateDates(pagamentoJurosNew);
  }

  const validationCache = React.useRef<Record<string, boolean>>({});

  const isDataPagamentoJurosValid = async (date: Date) => {
    const momentDate = moment(date);
    const dateStr = momentDate.format('YYYY-MM-DD');

    if (validationCache.current[dateStr] !== undefined) {
      return validationCache.current[dateStr];
    }

    if (!!minDate && momentDate.isBefore(minDate, 'day')) {
      validationCache.current[dateStr] = false;
      return false;
    }
    if (!!maxDate && momentDate.isAfter(maxDate, 'day')) {
      validationCache.current[dateStr] = false;
      return false;
    }

    try {
      const response = await getValidarDiaUtil(dispatch, date, false);
      const isValid = !!response.diaUtil;

      validationCache.current[dateStr] = isValid;
      return isValid;
    } catch (error) {
      console.error('Error validating date:', error);
      validationCache.current[dateStr] = false;
      return false;
    }
  };

  const validateSingleDate = async (rowId: string, date: Date) => {
    const isValid = await isDataPagamentoJurosValid(date);
    setInvalidDates(prev => ({
      ...prev,
      [rowId]: !isValid
    }));
    return isValid;
  };

  useEffect(() => {
    const validateDates = async () => {
      const newInvalidDates = { ...invalidDates };
      const validationPromises = [];

      for (const row of rows) {
        if (row.dataPagamentoJuros) {
          const promise = (async () => {
            const isValid = await isDataPagamentoJurosValid(row.dataPagamentoJuros);
            if (!isValid) {
              newInvalidDates[row.id] = true;
            } else {
              delete newInvalidDates[row.id];
            }
          })();
          validationPromises.push(promise);
        }
      }

      await Promise.all(validationPromises);
      setInvalidDates(newInvalidDates);
    };

    validateDates();
  }, [rows, minDate, maxDate, forceUpdate]);

  useEffect(() => {
    const validateCurrentRows = async () => {
      for (const row of rows) {
        if (row.dataPagamentoJuros && !row.isNew) {
          await validateSingleDate(row.id as string, row.dataPagamentoJuros as Date);
        }
      }
    };

    validateCurrentRows();
  }, [rows.length]);

  const getGridColumns: GridColumns = [
    {
      field: 'dataPagamentoJuros',
      headerName: 'Data de Pagamento de Juros',
      width: 250,
      type: 'date',
      editable: true,
      valueFormatter: (params: GridValueFormatterParams<Date>) => {
        return !params.value ? '' : moment(params.value).format('DD/MM/YYYY');
      },
      cellClassName: (params: GridCellParams<Date>) => {
        const date = params.value;
        return invalidDates[params.id] ? 'jurosError' : '';
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Ações',
      width: 200,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<CheckCircle fontSize="small" />}
              label="Save"
              className="textPrimary"
              onClick={handleSaveClick(id)}
              color="inherit"
              onResize={() => { }}
              onResizeCapture={() => { }}
            />,
            <GridActionsCellItem
              icon={<Cancel fontSize="small" />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
              onResize={() => { }}
              onResizeCapture={() => { }}
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<Edit fontSize="small" />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
            onResize={() => { }}
            onResizeCapture={() => { }}
          />,
          <GridActionsCellItem
            icon={<Delete fontSize="small" />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
            onResize={() => { }}
            onResizeCapture={() => { }}
          />,
        ];
      },
    },
  ];

  useEffect(() => {
    if (dates.length) {
      setRows(
        dates.map((date) => ({
          id: uuidv4(),
          dataPagamentoJuros: date,
          isNew: false,
        }))
      );
    }
  }, [dates]);

  return (
    <>
      <Section title={labels.pagamentoJuros}>
        <Grid container spacing={2} style={{ width: '100%' }}>
          <Grid item xs={12}>
            <SmSpace />
            <div className={classes.dataGridDiv}>
              <DataGridPro
                rows={rows}
                columns={getGridColumns}
                editMode="row"
                rowModesModel={rowModesModel}
                onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
                onRowEditStart={handleRowEditStart}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                components={{
                  Toolbar: EditToolbar,
                }}
                componentsProps={{ toolbar: { setRows, setRowModesModel } }}
                experimentalFeatures={{ newEditingApi: true }}
                pageSize={pageSize}
                checkboxSelection
                disableSelectionOnClick
                onSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
                selectionModel={selectionModel}
                localeText={localizedTextsMap}
              />
            </div>
          </Grid>
        </Grid>
      </Section>
    </>
  );
};
